import type { AnyAction } from 'redux';
import type {
  QuotingTabsType,
  RepairOrder,
  RepairOrderQuote,
  RepairOrderQuoteApprovalStatus,
  RepairOrderQuoteRecommendation,
  RepairOrderQuoteRecommendationPart,
} from 'types';

import { isEqual } from 'lodash';

import { QuotingTabs } from 'types';
import { sortRecommendationsBySortOrder } from 'utility/helpers/sorting';

import { QuotingActionTypes } from './action-types';

export type CustomerResponse = {
  repairOrderQuoteRecommendationId: number;
  approved: RepairOrderQuoteApprovalStatus; // theoretically a null should result in a 400
};

export type QuotingInitialState = {
  activeRecommendationId: number | null;
  activeTab: QuotingTabsType;
  customerResponses: CustomerResponse[];
  hasEditPermission: boolean;
  hasViewPermission: boolean;
  isConvertingMotorEstimateToRecommendation: boolean; // only one at a time
  isQuoteSaving: boolean;
  isEditingEnabled: boolean;
  isMyStatus: boolean;
  isQuoteLoading: boolean;
  isTotalsPending: boolean; // Make totals pending without making quote appear to be saving
  isUnlockingQuote: boolean;
  isUsingDms: boolean;
  queuedRecommendationChanges: number[];
  quote: RepairOrderQuote | null;
  repairOrder: RepairOrder | null;
  repairOrderImages: any[];
  recommendationChanges: { [key: number]: Partial<RepairOrderQuoteRecommendation> };
  hasCustomerApprovalChanges: boolean;
  invalidQuoteETagMessage: string | null;
  // ISA-2074 Story 1.5 (AD-3): the advisor's check-in "Skill (Dispatching)" picks, keyed by
  // recommendation id. Deliberately NOT part of recommendationChanges/autosave — a skill must
  // never persist onto the recommendation entity (that would make it push-readable on every
  // resend). Read by both Quick and Full Quote so one edit shows in both (AC 8), and serialized
  // onto the activate() request only.
  skillOverridesByRecommendationId: Partial<Record<number, string>>;
  skillLevelOptions: string[];
  isLoadingSkillLevelOptions: boolean;
};

export const quotingInitialState: QuotingInitialState = {
  activeRecommendationId: null,
  activeTab: QuotingTabs.QUOTE_VIEW,
  customerResponses: [],
  hasEditPermission: false,
  hasViewPermission: false,
  isConvertingMotorEstimateToRecommendation: false,
  isQuoteSaving: false,
  isEditingEnabled: false,
  isMyStatus: false,
  isQuoteLoading: false,
  isTotalsPending: false,
  isUnlockingQuote: false,
  isUsingDms: false,
  queuedRecommendationChanges: [],
  quote: null,
  repairOrder: null,
  repairOrderImages: [],
  recommendationChanges: {},
  hasCustomerApprovalChanges: false,
  invalidQuoteETagMessage: null,
  skillOverridesByRecommendationId: {},
  skillLevelOptions: [],
  isLoadingSkillLevelOptions: false,
};

function buildCustomerResponseArray(quote: RepairOrderQuote) {
  const recommendations = quote?.repairOrderQuoteRecommendations;
  if (!recommendations) return [];

  const customerResponses: CustomerResponse[] = recommendations
    .filter((recommendation) => {
      return recommendation.preApproved !== true;
    })
    .map((recommendation) => {
      return {
        repairOrderQuoteRecommendationId: recommendation.id,
        approved: recommendation.approved,
      };
    });

  return customerResponses;
}

// TODO assess SET_STATEs and create cases as necessary
const quotingReducer = (
  state: QuotingInitialState = quotingInitialState,
  action: AnyAction,
): QuotingInitialState => {
  switch (action.type) {
    case QuotingActionTypes.UPDATE_CUSTOMER_RESPONSE: {
      const customerResponse = action.data as CustomerResponse;
      const customerResponses = state.customerResponses.map((response) => {
        return response.repairOrderQuoteRecommendationId ===
          customerResponse.repairOrderQuoteRecommendationId
          ? customerResponse
          : response;
      });

      if (!state.quote) return { ...state };

      const isTotalsPending = !isEqual(buildCustomerResponseArray(state.quote), customerResponses);

      return { ...state, customerResponses, isTotalsPending, hasCustomerApprovalChanges: true };
    }
    case QuotingActionTypes.RESET_HAS_APPROVAL_CHANGES: {
      return { ...state, hasCustomerApprovalChanges: false };
    }
    case QuotingActionTypes.RECEIVE_RO_BY_QUOTE: {
      const repairOrder = action.data;
      return { ...state, repairOrder };
    }
    case QuotingActionTypes.UPDATE_QUOTE_TOTALS: {
      if (!state.quote) return state;

      const updatedTotals = action.data as RepairOrderQuote['totals'];
      const quote = { ...state.quote, totals: updatedTotals };

      return {
        ...state,
        isTotalsPending: false,
        quote,
      };
    }
    case QuotingActionTypes.RECEIVE_QUOTE: {
      const quote = action.data as RepairOrderQuote;
      const customerResponses = buildCustomerResponseArray(quote);
      const sortedRecommendations = sortRecommendationsBySortOrder(
        quote.repairOrderQuoteRecommendations,
      );

      return {
        ...state,
        customerResponses,
        isQuoteLoading: false,
        isTotalsPending: false,
        quote: {
          ...quote,
          repairOrderQuoteRecommendations: sortedRecommendations,
        },
      };
    }
    case QuotingActionTypes.SET_INVALID_QUOTE_ETAG_MESSAGE: {
      return {
        ...state,
        invalidQuoteETagMessage: action.data,
      };
    }
    case QuotingActionTypes.RECEIVE_QUOTE_FAILED:
      return { ...state, isQuoteLoading: false };
    case QuotingActionTypes.DELETE_QUOTE: {
      // TODO Remove this and set updated quote
      if (!state.quote) return state;
      const updated: RepairOrderQuote = { ...state.quote, deleted: true };

      return { ...state, isQuoteLoading: false, quote: updated };
    }
    case QuotingActionTypes.RECEIVE_PART: {
      if (!state.quote) return state;

      // Update parts on associated recommendation
      const updatedPart = action.data as RepairOrderQuoteRecommendationPart;
      const updatedRepairOrderQuoteRecommendations =
        state.quote.repairOrderQuoteRecommendations.map((rec) => {
          if (rec.id === updatedPart.repairOrderRecommendation?.id) {
            const updatedRepairOrderQuoteRecommendationParts =
              rec.repairOrderQuoteRecommendationParts?.map((existingPart) => {
                return existingPart.id === updatedPart.id ? updatedPart : existingPart;
              }) ?? [];
            return {
              ...rec,
              repairOrderQuoteRecommendationParts: updatedRepairOrderQuoteRecommendationParts,
            };
          } else {
            return rec;
          }
        });
      const updatedQuote = {
        ...state.quote,
        repairOrderQuoteRecommendations: updatedRepairOrderQuoteRecommendations,
      };
      return { ...state, isQuoteLoading: false, quote: updatedQuote };
    }
    case QuotingActionTypes.SET_RECOMMENDATION_CHANGES: {
      return {
        ...state,
        recommendationChanges: {
          ...state.recommendationChanges,
          [action.data.id]: action.data.updatedChanges,
        },
      };
    }
    case QuotingActionTypes.RESET_RECOMMENDATION_CHANGES: {
      return {
        ...state,
        recommendationChanges: {},
      };
    }
    case QuotingActionTypes.RESET_ACTIVE_QUOTE: {
      return { ...state, quote: null };
    }
    case QuotingActionTypes.SET_SKILL_OVERRIDE: {
      const { recommendationId, skillLevel } = action.data as {
        recommendationId: number;
        skillLevel: string | null;
      };
      const skillOverridesByRecommendationId = { ...state.skillOverridesByRecommendationId };

      // A blank/cleared selection is removed rather than stored as '' — this is what makes the
      // "picked then removed" edge case send no SkillLevel at all (an absent key, not an empty
      // string) when the map is serialized onto the activate() request.
      if (skillLevel) {
        skillOverridesByRecommendationId[recommendationId] = skillLevel;
      } else {
        delete skillOverridesByRecommendationId[recommendationId];
      }

      return { ...state, skillOverridesByRecommendationId };
    }
    case QuotingActionTypes.RESET_SKILL_OVERRIDES:
      return { ...state, skillOverridesByRecommendationId: {} };
    case QuotingActionTypes.FETCH_SKILL_LEVEL_OPTIONS:
      return { ...state, isLoadingSkillLevelOptions: action.data as boolean };
    case QuotingActionTypes.RECEIVE_SKILL_LEVEL_OPTIONS:
      return { ...state, skillLevelOptions: action.data as string[] };
    case QuotingActionTypes.SET_STATE:
      return { ...state, ...action.data };
    default:
      return state;
  }
};

export default quotingReducer;
